import type { Factor, Scale, Scenario } from "../data/schema";

/**
 * THE SLIDER ENGINE
 * =================
 * Every slider produces a position `t` in [0, 1]. We map that position to a
 * value using the factor's three anchors:
 *
 *     t = 0.0  -> pessimistic
 *     t = 0.5  -> consensus     (the midpoint notch)
 *     t = 1.0  -> optimistic
 *
 * Between anchors we interpolate. For "log" factors we interpolate in
 * log-space so a slider feels natural across orders of magnitude (e.g.
 * 1e-6 .. 0.1 .. 1). For "linear" factors we interpolate plainly.
 *
 * Pinning consensus to the exact middle means the named scenarios always land
 * on clean slider positions (0, 0.5, 1) and the consensus notch is meaningful.
 */

/** Slider position for each named scenario. */
export const SCENARIO_T: Record<Scenario, number> = {
  pessimistic: 0,
  consensus: 0.5,
  optimistic: 1,
};

function lerp(a: number, b: number, u: number): number {
  return a + (b - a) * u;
}

/** Interpolate between a and b, in log-space when requested and safe. */
function interpolate(a: number, b: number, u: number, scale: Scale): number {
  // Log interpolation needs strictly positive endpoints; otherwise fall back.
  if (scale === "log" && a > 0 && b > 0) {
    return Math.exp(lerp(Math.log(a), Math.log(b), u));
  }
  return lerp(a, b, u);
}

/** Resolve a factor's value at slider position `t` (clamped to [0,1]). */
export function valueAt(factor: Factor, t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  const { pessimistic, consensus, optimistic } = factor.values;
  if (clamped <= 0.5) {
    return interpolate(pessimistic, consensus, clamped / 0.5, factor.scale);
  }
  return interpolate(consensus, optimistic, (clamped - 0.5) / 0.5, factor.scale);
}

/** A single step in the collapse: the factor and the running product after it. */
export interface CollapseStep {
  factor: Factor;
  /** The chosen value for this factor at its current slider position. */
  value: number;
  /** Running product of the chain up to and including this factor. */
  runningTotal: number;
}

export interface CalculationResult {
  /** The value the chain starts from (meta.startingValue, or 1). */
  start: number;
  /** Ordered steps, one per factor, in dataset order. */
  steps: CollapseStep[];
  /** The final collapsed number. */
  total: number;
}

/**
 * Run the whole chain. `positions` maps factor.id -> slider t. Any factor not
 * present falls back to consensus (0.5). Factors are applied in the order they
 * appear in `factors` so the funnel reads top-to-bottom.
 */
export function calculate(
  factors: Factor[],
  positions: Record<string, number>,
  start = 1,
): CalculationResult {
  let runningTotal = start;
  const steps: CollapseStep[] = factors.map((factor) => {
    const t = positions[factor.id] ?? SCENARIO_T.consensus;
    const value = valueAt(factor, t);
    runningTotal *= value;
    return { factor, value, runningTotal };
  });
  return { start, steps, total: runningTotal };
}

/**
 * One "act" of the collapse: a category, its steps, and the running total as
 * the chain *enters* and *exits* that category. `multiplier` is exit/enter —
 * how much this category alone shrank the number.
 */
export interface ActBreakdown {
  categoryId: string;
  steps: CollapseStep[];
  enter: number;
  exit: number;
  multiplier: number;
}

/**
 * Slice a finished calculation into per-category acts, in the order the
 * categories are given. Assumes factors are grouped by category in dataset
 * order (they are), so each act is a contiguous run of steps.
 */
export function actBreakdown(
  result: CalculationResult,
  categoryIds: string[],
): ActBreakdown[] {
  let enter = result.start;
  return categoryIds
    .map((categoryId) => {
      const steps = result.steps.filter((s) => s.factor.category === categoryId);
      if (steps.length === 0) return null;
      const exit = steps[steps.length - 1].runningTotal;
      const act: ActBreakdown = {
        categoryId,
        steps,
        enter,
        exit,
        multiplier: enter > 0 ? exit / enter : 0,
      };
      enter = exit;
      return act;
    })
    .filter((a): a is ActBreakdown => a !== null);
}

/** Default slider positions: every factor at consensus. */
export function defaultPositions(factors: Factor[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const f of factors) out[f.id] = SCENARIO_T.consensus;
  return out;
}

/** Positions that snap every factor to one named scenario. */
export function scenarioPositions(
  factors: Factor[],
  scenario: Scenario,
): Record<string, number> {
  const t = SCENARIO_T[scenario];
  const out: Record<string, number> = {};
  for (const f of factors) out[f.id] = t;
  return out;
}
